import { ElementType, Suspense, useEffect } from "react";
import { Navigate, Outlet, useLocation, useRoutes } from "react-router-dom";
// hooks
import useAuth from "../hooks/useAuth";
import AuthDashboardLayout from "../layouts/authdashboard";
import DashboardLayout from "../layouts/dashboard";
import LogoOnlyLayout from "../layouts/LogoOnlyLayout";
// guards
import AuthGuard from "../guards/AuthGuard";
import GuestGuard from "../guards/GuestGuard";
// import RoleBasedGuard from '../guards/RoleBasedGuard';
// config
import { PATH_AFTER_LOGIN } from "../config";
import { PATH_PAYABLES, PATH_PROCUREMENT } from "./paths";
// components
import PermissionBlocker from "@components/PermissionBlocker";
import {
  DeliveryNote,
  FranceRegularInvoice,
  PurchaseOrder as PurchaseOrderManualDocument,
  SDICreditNote,
  SDIRegularInvoice,
  SIICorrectiveInvoice,
  SIICreditNote,
  SIIRegularInvoice,
  SIIReplacementInvoice,
  SIISimplifiedInvoice,
  VerifactuCorrectiveInvoice,
  VerifactuCreditNote,
  VerifactuDebitNote,
  VerifactuRegularInvoice,
  VerifactuSimplifiedInvoice,
} from "@pages/ManualDocumentV2";
import PurchaseRequisitionDrafts from "@pages/procurement/components/PRList/PurchaseRequisitionDrafts";
import { Permissions } from "src/@types/permissions";
import { GetAsync } from "src/common/httpRequests";
import { API_GATEWAY } from "src/constants/settings";
import { LoginCred } from "src/redux/slices/credentials";
import { planUpdate } from "src/redux/slices/organization";
import { RootState, store, useSelector } from "src/redux/store";
import LoadingScreen from "../components/LoadingScreen";
import ProcurementPODrafts from "../pages/procurement/components/PODraftList/ProcurementPODrafts";
import lazyWithRetry from "./../lazyWithRetry";

// ----------------------------------------------------------------------

const Loadable = (Component: ElementType) => (props: any) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { isAuthenticated } = useAuth();

  const isDashboard =
    isAuthenticated &&
    ["/dashboard", "/configuration", "/catalog", "/payments", "/report", "/tasks"].some(
      (path) => pathname.includes(path),
    );

  return (
    <Suspense fallback={<LoadingScreen isDashboard={isDashboard} />}>
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  const location = useLocation();
  const {
    credential,
    organization: {
      org: { enableAskDost, enableStandardization },
    },
  } = store.getState();

  const {
    userTokenDecode: { email },
  } = credential;

  const fetchProfile = async () => {
    const res = await GetAsync(`${API_GATEWAY}/api/userservice/Profile`);
    try {
      if (res.status === 200) {
        store.dispatch(planUpdate(res.data.organizationSubscribedPlan));
        const userCred = store.getState()?.credential?.userCred as any;
        if (userCred?.accessToken && res.data.firstName && res.data.firstName !== userCred.firstName) {
          store.dispatch(LoginCred({ ...userCred, firstName: res.data.firstName }, userCred?.email));
        }
      }
    } catch (e) {}
  };

  const MixpanelWrapper = ({ children }) => {
    useEffect(() => {
      // window.Intercom("update");
      // if (location.pathname !== "/auth/external-identity") {
      //   MixpanelFunction({
      //     eventName: "Page Viewed",
      //     url: location.pathname,
      //   });
      // }

      if (
        location.pathname !== "/auth/external-identity" &&
        location.pathname !== "/auth/login" &&
        email
      ) {
        fetchProfile();
      }
    }, []);

    return children;
  };

  return useRoutes([
    {
      path: "auth",
      element: (
        <MixpanelWrapper>
          <GuestGuard>
            <AuthDashboardLayout />
          </GuestGuard>
        </MixpanelWrapper>
      ),
      children: [
        {
          path: "login",
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          ),
        },
        {
          path: "register",
          element: (
            <GuestGuard>
              <Register />
            </GuestGuard>
          ),
        },
        {
          path: "new-register",
          element: (
            <GuestGuard>
              <NewRegister />
            </GuestGuard>
          ),
        },
        {
          path: "login-unprotected",
          element: <Login />,
        },
        {
          path: "register-unprotected",
          element: <Register />,
        },
        {
          path: "reset-password",
          element: <ResetPassword />,
        },
        {
          path: "new-password",
          element: <NewPassword />,
        },
        // {path: 'verify', element: <VerifyCode /> },
        {
          path: "pre-register",
          element: <PreRegister />,
        },
        {
          path: "pre-verify",
          element: <PreVerify />,
        },
        {
          path: "external-identity",
          element: <ExternalIdentity />,
        },
        {
          path: "password",
          children: [
            {
              path: "reset",
              element: <ResetPasswordV2 />,
            },
          ],
        },
      ],
    },

    {
      path: "impersonate",
      element: (
        <MixpanelWrapper>
          <Impersonate />
        </MixpanelWrapper>
      ),
    },

    {
      path: "discrepancy",
      element: <SendDiscrepancyToSupplier />,
    },
    {
      path: "connector",
      element: (
        <MixpanelWrapper>
          <AuthGuard>
            <DashboardLayout />
          </AuthGuard>
        </MixpanelWrapper>
      ),
      children: [
        {
          path: "oAuthRedirect",
          element: <RedirectionPage />,
        },
      ],
    },
    {
      path: "paymentgateway",
      element: (
        <MixpanelWrapper>
          <AuthGuard>
            <DashboardLayout />
          </AuthGuard>
        </MixpanelWrapper>
      ),
      children: [
        {
          path: "oAuthRedirect",
          element: <PaymentGatewayOAuthRedirectionPage />,
        },
      ],
    },
    {
      path: "notificationapp",
      element: (
        <MixpanelWrapper>
          <AuthGuard>
            <DashboardLayout />
          </AuthGuard>
        </MixpanelWrapper>
      ),
      children: [
        {
          path: "oAuthRedirect",
          element: <NotificationAppOAuthRedirectionPage />,
        },
      ],
    },
    {
      path: "dashboard",
      element: (
        <MixpanelWrapper>
          <AuthGuard>
            <DashboardLayout />
          </AuthGuard>
        </MixpanelWrapper>
      ),
      children: [
        { element: <Components />, path: "components" },
        { element: <AnalyticsV2 />, path: "analyticsV2" },
        { element: <AnalyticsV3 />, path: "analytics" },
        { element: <Navigate to={PATH_AFTER_LOGIN} replace />, index: true },
        {
          path: "app",
          element: <GeneralApp />,
        },
        {
          path: "documents",
          element: (
            <>
              <Outlet />
            </>
          ),
          children: [
            {
              path: "",
              element: <Finance />,
            },

            {
              path: "review",
              element: <DocumentReviewRouter />,
            },
            { path: "mailinbox", element: <MailInbox /> },
            {
              path: "create-purchase-order",
              element: <PurchaseOrderManualDocument />,
            },
            {
              path: "create-sale-delivery-note",
              element: <DeliveryNote />,
            },
            {
              path: "verifactu",
              element: <Outlet />,
              children: [
                {
                  path: "create-regular-invoice",
                  element: <VerifactuRegularInvoice />,
                },
                {
                  path: "create-simplified-invoice",
                  element: <VerifactuSimplifiedInvoice />,
                },
                {
                  path: "create-debit-note",
                  element: <VerifactuDebitNote />,
                },
                {
                  path: "create-credit-note",
                  element: <VerifactuCreditNote />,
                },
                {
                  path: "create-corrective-invoice",
                  element: <VerifactuCorrectiveInvoice />,
                },
              ],
            },
            {
              path: "sdi",
              element: <Outlet />,
              children: [
                {
                  path: "create-regular-invoice",
                  element: <SDIRegularInvoice />,
                },
                {
                  path: "create-credit-note",
                  element: <SDICreditNote />,
                },
              ],
            },
            {
              path: "sii",
              element: <Outlet />,
              children: [
                {
                  path: "create-regular-invoice",
                  element: <SIIRegularInvoice />,
                },
                {
                  path: "create-simplified-invoice",
                  element: <SIISimplifiedInvoice />,
                },
                {
                  path: "create-credit-note",
                  element: <SIICreditNote />,
                },
                {
                  path: "create-corrective-invoice",
                  element: <SIICorrectiveInvoice />,
                },
                {
                  path: "create-replacement-form",
                  element: <SIIReplacementInvoice />,
                },
              ],
            },
            {
              path: "france",
              element: <Outlet />,
              children: [
                {
                  path: "create-regular-invoice",
                  element: <FranceRegularInvoice />,
                },
              ],
            },
          ],
        },

        {
          path: "developers-settings",
          element: <DevelopersSettings />,
        },
        { path: "integration", element: <Integration /> },
        {
          path: "comingSoon",
        },
        {
          path: "workflow",
          element: <Workflows />,
        },
        {
          path: "workflow-templates",
          element: (
            <PermissionBlocker
              permissions={[
                Permissions.WorkflowManagement_Read,
                Permissions.WorkflowManagement_Write,
              ]}
            >
              <WorkflowTemplates />
            </PermissionBlocker>
          ),
        },
        {
          path: "suppliers",
          element: <Suppliers />,
        },
        {
          path: "suppliers/legal-documents",
          element: (
            <PermissionBlocker
              permissions={[
                Permissions.Suppliers_Read,
                Permissions.Suppliers_Write,
              ]}
            >
              <SupplierLegalDocuments />
            </PermissionBlocker>
          ),
        },
        {
          path: "suppliers/customise-onboarding",
          element: (
            <PermissionBlocker
              permissions={[Permissions.Supplier_OnboardingTemplate_View]}
            >
              <OnboardingTemplateTabs />
            </PermissionBlocker>
          ),
        },
        {
          path: "customers",
          element: <Customers />,
        },
        {
          path: "usersV2",
          element: <Users />,
        },
        {
          path: "companies",
          element: (
            <PermissionBlocker
              permissions={[
                Permissions.Company_Read,
                Permissions.Company_Write,
              ]}
            >
              <Companies />
            </PermissionBlocker>
          ),
        },
        {
          path: "upgrade",
          element: <Upgrade />,
        },
        {
          path: "feedback",
          element: <FeedBack />,
        },
        {
          path: "notifications",
          element: <Notification />,
        },
        {
          path: "stripe",
          element: <Stripe />,
        },
        {
          path: "paymentSuccess",
          element: <PaymentSuccess />,
        },

        { path: "analytics", element: <Analytics /> },
        ...(enableAskDost
          ? [
              {
                path: "ask-dost",
                element: <AskDostFullPage />,
              },
            ]
          : []),
        {
          path: "error",
          children: [{ path: "*", element: <Navigate to="/404" replace /> }],
        },
        {
          path: "user",
          // element: <LogoOnlyLayout />,
          children: [
            {
              path: "verification",
              element: <AccountVerification />,
            },
            {
              path: "account",
              element: <MyAccount />,
            },
          ],
        },
      ],
    },
    {
      path: "catalog",
      element: (
        <MixpanelWrapper>
          <AuthGuard>
            <DashboardLayout />
          </AuthGuard>
        </MixpanelWrapper>
      ),
      children: [
        { element: <CatalogItems />, path: "items" },
        { element: <Taxes />, path: "taxes" },
        {
          path: "currencies",
          element: (
            <PermissionBlocker permissions={[Permissions.Catalog_Currencies_View]}>
              <Currencies />
            </PermissionBlocker>
          ),
        },
        {
          path: "payment-methods",
          element: (
            <PermissionBlocker
              permissions={[Permissions.Catalog_PaymentMethods_View]}
            >
              <PaymentMethods />
            </PermissionBlocker>
          ),
        },
        {
          path: "payment-terms",
          element: (
            <PermissionBlocker
              permissions={[Permissions.Catalog_PaymentTerms_View]}
            >
              <PaymentTerms />
            </PermissionBlocker>
          ),
        },
      ],
    },
    {
      path: "report",
      element: (
        <MixpanelWrapper>
          <AuthGuard>
            <DashboardLayout />
          </AuthGuard>
        </MixpanelWrapper>
      ),
      children: [
        { element: <Reports />, path: "" },
        { element: <WorkflowAnalytics />, path: "workflow" },
        { element: <DocumentUsage />, path: "document-usage" },
        { element: <SelfTrainingReportV2 />, path: "selftraining" },
        { element: <YearWiseDocSummary />, path: "yearwise-document-summary" },
        { element: <CustomReports />, path: "custom-reports/:id" },
        { element: <CustomerPayments />, path: "customer-payment" },
        { element: <DiscrepancySummary />, path: "discrepancy" },
      ],
    },
    {
      path: "payments",
      element: (
        <MixpanelWrapper>
          <AuthGuard>
            <DashboardLayout />
          </AuthGuard>
        </MixpanelWrapper>
      ),
      children: [
        { path: "bank-accounts", element: <BankAccounts /> },
        { path: "invoice-payments", element: <Payments /> },
        { path: "bank-transactions", element: <Transactions /> },
        { path: "payment-schedule", element: <PaymentSchedule /> },
        { path: "bank-reconciliation", element: <BankReconciliation /> },
        { path: "bank-reconciliationV2", element: <BankReconciliationV2 /> },
      ],
    },
    {
      path: "expense",
      element: (
        <MixpanelWrapper>
          <AuthGuard>
            <DashboardLayout />
          </AuthGuard>
        </MixpanelWrapper>
      ),
      children: [
        { path: "employees", element: <Employee /> },
        { path: "expenses", element: <Expense /> },
      ],
    },
    {
      path: "receivables",
      element: (
        <MixpanelWrapper>
          <AuthGuard>
            <DashboardLayout />
          </AuthGuard>
        </MixpanelWrapper>
      ),
      children: [
        {
          path: "sale-invoice",
          element: <Outlet />,
          children: [
            { index: true, element: <DocumentList /> },
            {
              path: "drafts",
              element: (
                <PermissionBlocker
                  permissions={[
                    Permissions.Documents_Read,
                    Permissions.Documents_Write,
                  ]}
                >
                  <ManualDocumentDrafts />
                </PermissionBlocker>
              ),
            },
          ],
        },
        {
          path: "sale-order",
          element: <DocumentList />,
        },

        {
          path: "sale-delivery-note",
          element: <Outlet />,
          children: [
            { index: true, element: <DocumentList /> },
            {
              path: "drafts",
              element: (
                <PermissionBlocker
                  permissions={[
                    Permissions.Documents_Read,
                    Permissions.Documents_Write,
                  ]}
                >
                  <ManualDocumentDrafts />
                </PermissionBlocker>
              ),
            },
          ],
        },
        {
          path: "unclassified",
          element: <DocumentList />,
        },
      ],
    },
    {
      path: "training",
      element: (
        <MixpanelWrapper>
          <AuthGuard>
            <DashboardLayout />
          </AuthGuard>
        </MixpanelWrapper>
      ),
      children: [
        { path: "self-training", element: <SelfTrainingRouter /> },
        {
          path: "self-training/:projectId/documents",
          element: <ProjectDocumentsPage />,
          children: [{ path: ":documentId", element: null }],
        },
        { path: "extraction-rules", element: <ExtractionRules /> },
      ],
    },
    {
      path: "tasks",
      element: (
        <MixpanelWrapper>
          <AuthGuard>
            <DashboardLayout />
          </AuthGuard>
        </MixpanelWrapper>
      ),
      children: [{ path: "", element: <Tasks /> }],
    },
    {
      path: "dimension",
      element: (
        <MixpanelWrapper>
          <AuthGuard>
            <DashboardLayout />
          </AuthGuard>
        </MixpanelWrapper>
      ),
      children: [
        {
          path: "",
          element: (
            <PermissionBlocker
              permissions={[Permissions.Catalog_Dimensions_View]}
            >
              <Dimension />
            </PermissionBlocker>
          ),
        },
        {
          path: ":id/dimension-values",
          element: (
            <PermissionBlocker
              permissions={[Permissions.Catalog_DimensionValues_View]}
            >
              <DimensionValues />
            </PermissionBlocker>
          ),
        },
      ],
    },

    {
      path: "payables",
      element: (
        <MixpanelWrapper>
          <AuthGuard>
            <DashboardLayout />
          </AuthGuard>
        </MixpanelWrapper>
      ),
      children: [
        {
          path: "purchase-invoice",
          element: <DocumentList />,
        },
        {
          path: "purchase-order",
          element: <Outlet />,
          children: [
            {
              index: true,
              // Standardization lists purchase orders under Procurement
              element: enableStandardization ? (
                <Navigate
                  to={`${PATH_PROCUREMENT.general.purchaseOrder.root}${location.search}`}
                  replace
                />
              ) : (
                <DocumentList />
              ),
            },
            {
              path: "drafts",
              element: (
                <PermissionBlocker
                  permissions={[
                    Permissions.Documents_Read,
                    Permissions.Documents_Write,
                  ]}
                >
                  <ManualDocumentDrafts />
                </PermissionBlocker>
              ),
            },
          ],
        },
        {
          path: "purchase-deliverynote",
          element: <DocumentList />,
        },
        {
          path: "unclassified",
          element: <DocumentList />,
        },
      ],
    },

    // Configuration
    {
      path: "configuration",
      element: (
        <MixpanelWrapper>
          <AuthGuard>
            <DashboardLayout />
          </AuthGuard>
        </MixpanelWrapper>
      ),
      children: [
        { index: true, element: <ConfigurationLanding /> },
        {
          element: <DocumentsConfiguration />,
          path: "document-configuration",
        },
        {
          element: <DiscrepancyConfiguration />,
          path: "discrepancy-configuration",
        },
        { element: <InputCSVMapping />, path: "input-csv-mapping" },
        { element: <OutputCSVMapping />, path: "output-csv-mapping" },
        {
          element: <ExpectedValuesConfiguration />,
          path: "predefined-values-configuration",
        },
        { element: <ERPSyncConfiguration />, path: "erp-sync-configuration" },
        { element: <WorkflowConfiguration />, path: "workflow" },
        {
          element: (
            <PermissionBlocker
              permissions={[Permissions.AP_CodingTemplate_View]}
            >
              <CodingTemplatesConfiguration />
            </PermissionBlocker>
          ),
          path: "coding-templates",
        },
        {
          element: (
            <PermissionBlocker
              permissions={[Permissions.AP_PostingDatePolicy_View]}
            >
              <PostingDatePolicyConfiguration />
            </PermissionBlocker>
          ),
          path: "posting-date-policy",
        },
        {
          element: <PeppolPartyRegistrationConfiguration />,
          path: "peppol-party-registration",
        },
        {
          element: (
            <PermissionBlocker
              permissions={[
                Permissions.DocumentConfigurations_Read,
                Permissions.DocumentConfigurations_Write,
              ]}
            >
              <ExtractionConfiguration />
            </PermissionBlocker>
          ),
          path: "extraction-configuration",
        },
        {
          element: (
            <PermissionBlocker
              permissions={[
                Permissions.DocumentConfigurations_Read,
                Permissions.DocumentConfigurations_Write,
              ]}
            >
              <ValidationConfiguration />
            </PermissionBlocker>
          ),
          path: "validation-configuration",
        },
      ],
    },

    // Procurement
    {
      path: "procurement",
      element: (
        <MixpanelWrapper>
          <AuthGuard>
            <DashboardLayout />
          </AuthGuard>
        </MixpanelWrapper>
      ),
      children: [
        {
          path: "purchase-requisition",
          element: <Outlet />,
          children: [
            {
              path: "list",
              element: (
                <PermissionBlocker
                  permissions={[
                    Permissions.Procurement_PurchaseRequisition_View,
                  ]}
                >
                  <DocumentList
                    routeOverride={{
                      accountClass: "payables",
                      documentClass: "purchase-requisition",
                    }}
                  />
                </PermissionBlocker>
              ),
            },
            {
              path: "create",
              element: (
                <PermissionBlocker
                  permissions={[
                    Permissions.Procurement_PurchaseRequisition_Create,
                    Permissions.Procurement_PurchaseRequisition_Update,
                  ]}
                >
                  <PurchaseRequisition />
                </PermissionBlocker>
              ),
            },
            {
              path: "drafts",
              element: (
                <PermissionBlocker
                  permissions={[
                    Permissions.Procurement_PurchaseRequisition_Create,
                    Permissions.Procurement_PurchaseRequisition_Update,
                    Permissions.Procurement_PurchaseRequisition_View,
                  ]}
                >
                  <PurchaseRequisitionDrafts />
                </PermissionBlocker>
              ),
            },
          ],
        },
        {
          path: "purchase-order",
          element: <Outlet />,
          children: [
            {
              index: true,
              element: enableStandardization ? (
                <DocumentList />
              ) : (
                <Navigate
                  to={`${PATH_PAYABLES.general.purchaseOrder.root}${location.search}`}
                  replace
                />
              ),
            },
            {
              path: "create",
              element: (
                <PermissionBlocker
                  permissions={[
                    Permissions.Procurement_PurchaseOrder_Create,
                    Permissions.Procurement_PurchaseOrder_Update,
                  ]}
                >
                  <ProcurementPurchaseOrder />
                </PermissionBlocker>
              ),
            },
            {
              path: "drafts",
              element: (
                <PermissionBlocker
                  permissions={[
                    Permissions.Procurement_PurchaseOrder_Create,
                    Permissions.Procurement_PurchaseOrder_Update,
                    Permissions.Procurement_PurchaseOrder_View,
                  ]}
                >
                  <ProcurementPODrafts />
                </PermissionBlocker>
              ),
            },
          ],
        },
        {
          path: "grn",
          element: <Outlet />,
          children: [
            {
              path: "list",
              element: (
                <PermissionBlocker
                  permissions={[Permissions.Procurement_GoodsReceivedNote_View]}
                >
                  <DocumentList
                    routeOverride={{
                      accountClass: "payables",
                      documentClass: "grn",
                    }}
                  />
                </PermissionBlocker>
              ),
            },
          ],
        },
      ],
    },

    // Main Routes
    {
      path: "*",
      element: (
        <MixpanelWrapper>
          <LogoOnlyLayout />
        </MixpanelWrapper>
      ),
      children: [{ path: "*", element: <Navigate to="/404" replace /> }],
    },
    {
      path: "404",
      element: (
        <MixpanelWrapper>
          <AuthDashboardLayout />
        </MixpanelWrapper>
      ),
      children: [{ path: "", element: <Page404 /> }],
    },
    {
      path: "403",
      element: (
        <MixpanelWrapper>
          <AuthDashboardLayout />
        </MixpanelWrapper>
      ),
      children: [{ path: "", element: <Page403 /> }],
    },
    {
      path: "user",
      // element: <LogoOnlyLayout />,
      children: [
        {
          path: "verification",
          element: <AccountVerification />,
        },
        {
          path: "account",
          element: <MyAccount />,
        },
      ],
    },
    {
      path: "/",
      element: (
        <MixpanelWrapper>
          <GuestGuard>
            <AuthDashboardLayout />
          </GuestGuard>
        </MixpanelWrapper>
      ),
      children: [{ path: "/", element: <Navigate to="auth/login" replace /> }],
    },
    { path: "microsoftLogin" },
    {
      path: "showOneDrive",
      element: (
        <MixpanelWrapper>
          <ShowOneDrive />
        </MixpanelWrapper>
      ),
    },
    {
      path: "gdrive",
      element: (
        <MixpanelWrapper>
          <GDrive />
        </MixpanelWrapper>
      ),
    },

    { path: "*", element: <Navigate to="/404" replace /> },
  ]);
}

// AUTHENTICATION
const Login = Loadable(lazyWithRetry(() => import("../pages/auth/Login")));
const Register = Loadable(
  lazyWithRetry(() => import("../pages/auth/Register")),
);
const NewRegister = Loadable(
  lazyWithRetry(() => import("../pages/auth/NewRegister")),
);

const ResetPassword = Loadable(
  lazyWithRetry(() => import("../pages/auth/ResetPassword")),
);
const ResetPasswordV2 = Loadable(
  lazyWithRetry(() => import("../pages/auth/ResetPasswordV2")),
);
const NewPassword = Loadable(
  lazyWithRetry(() => import("../pages/auth/NewPassword")),
);
// const VerifyCode = Loadable(lazyWithRetry(() => import("../pages/auth/VerifyCode")));
const PreVerify = Loadable(
  lazyWithRetry(() => import("src/pages/auth/PreVerify")),
);
const ExternalIdentity = Loadable(
  lazyWithRetry(() => import("src/pages/auth/ExternalIdentity")),
);
const Integration = Loadable(
  lazyWithRetry(() => import("src/pages/integration/index")),
);
const ConfigurationLanding = Loadable(
  lazyWithRetry(() => import("src/pages/configuration")),
);
const DocumentsConfiguration = Loadable(
  lazyWithRetry(
    () => import("src/pages/configuration/DocumentConfigurationV2"),
  ),
);
const DiscrepancyConfiguration = Loadable(
  lazyWithRetry(
    () => import("src/pages/configuration/DiscrepancyConfiguration"),
  ),
);
const InputCSVMapping = Loadable(
  lazyWithRetry(() => import("src/pages/configuration/MappingConfigurationV2")),
);

const OutputCSVMapping = Loadable(
  lazyWithRetry(
    () => import("src/pages/configuration/OutputCSVMapping/components/index"),
  ),
);
const ExpectedValuesConfiguration = Loadable(
  lazyWithRetry(
    () =>
      import(
        "src/pages/configuration/DocumentConfigurationV2/ExpectedValuesConfiguration/ExpectedValueTabs"
      ),
  ),
);
const ERPSyncConfiguration = Loadable(
  lazyWithRetry(() => import("src/pages/configuration/ERPSyncConfiguration")),
);

const WorkflowConfiguration = Loadable(
  lazyWithRetry(() => import("src/pages/configuration/WorkflowConfiguration")),
);

const CodingTemplatesConfiguration = Loadable(
  lazyWithRetry(() => import("src/pages/configuration/CodingTemplates")),
);

const PostingDatePolicyConfiguration = Loadable(
  lazyWithRetry(() => import("src/pages/configuration/PostingDatePolicy")),
);

const ValidationConfiguration = Loadable(
  lazyWithRetry(() => import("src/pages/configuration/ValidationConfiguration")),
);
const ExtractionConfiguration = Loadable(
  lazyWithRetry(() => import("src/pages/configuration/ExtractionConfiguration")),
);

const PeppolPartyRegistrationConfiguration = Loadable(
  lazyWithRetry(
    () => import("src/pages/configuration/PeppolPartyRegistration"),
  ),
);

const CatalogItems = Loadable(
  lazyWithRetry(() => import("src/pages/Catalog/Items")),
);
const Taxes = Loadable(lazyWithRetry(() => import("src/pages/Catalog/Taxes")));
const Currencies = Loadable(
  lazyWithRetry(() => import("src/pages/Catalog/Currencies")),
);
const PaymentMethods = Loadable(
  lazyWithRetry(() => import("src/pages/Catalog/PaymentMethods")),
);
const PaymentTerms = Loadable(
  lazyWithRetry(() => import("src/pages/Catalog/PaymentTerms")),
);

const GeneralApp = Loadable(
  lazyWithRetry(() => import("src/pages/DashboardV2/index")),
);
const Suppliers = Loadable(
  lazyWithRetry(() => import("src/pages/Suppliers/index")),
);
const SupplierLegalDocuments = Loadable(
  lazyWithRetry(() => import("src/pages/SupplierLegalDocuments/index")),
);
const OnboardingTemplateTabs = Loadable(
  lazyWithRetry(() => import("src/pages/Suppliers/OnboardingTemplate/index")),
);
const Customers = Loadable(
  lazyWithRetry(() => import("src/pages/Customers/index")),
);
const Users = Loadable(lazyWithRetry(() => import("src/pages/Users/Users")));

const Companies = Loadable(
  lazyWithRetry(() => import("@pages/Company/components")),
);

const ShowOneDrive = Loadable(
  lazyWithRetry(() => import("src/pages/showOneDrive/index")),
);
const GDrive = Loadable(
  lazyWithRetry(() => import("src/pages/showGDrive/index")),
);

const Upgrade = Loadable(
  lazyWithRetry(() => import("src/pages/upgrade/index")),
);
const Notification = Loadable(
  lazyWithRetry(() => import("src/pages/Notification/Notifications")),
);

const Stripe = Loadable(
  lazyWithRetry(() => import("src/pages/PaymentMethod/stripe/index")),
);
const PaymentSuccess = Loadable(
  lazyWithRetry(() => import("src/pages/paymentSuccess/index")),
);

const Finance = Loadable(
  lazyWithRetry(() => import("src/pages/Documents/Finance/table/table")),
);

const DocumentList = Loadable(
  lazyWithRetry(() => import("@pages/DocumentsV2/DocumentList")),
);

// Drafts
const ManualDocumentDrafts = Loadable(
  lazyWithRetry(() => import("@pages/DocumentsV2/Drafts/components")),
);

const Payments = Loadable(
  lazyWithRetry(() => import("src/pages/Payments/InvoicePayments")),
);

const BankAccounts = Loadable(
  lazyWithRetry(() => import("src/pages/PaymentsV2/components/bankAccounts")),
);

const Transactions = Loadable(
  lazyWithRetry(() => import("src/pages/Payments/Transactions")),
);

const PaymentSchedule = Loadable(
  lazyWithRetry(() => import("src/pages/Payments/Schedules")),
);

const BankReconciliation = Loadable(
  lazyWithRetry(() => import("src/pages/Payments/BankReconciliation/index")),
);

const BankReconciliationV2 = Loadable(
  lazyWithRetry(
    () => import("src/pages/PaymentsV2/components/bankReconciliation/index"),
  ),
);
const Analytics = Loadable(lazyWithRetry(() => import("src/pages/Analytics")));

//Feedback
const FeedBack = Loadable(
  lazyWithRetry(() => import("src/pages/feedback/index")),
);

const FormValidation = Loadable(
  lazyWithRetry(() => import("src/pages/formvalidation")),
);

const DocumentReviewV2 = Loadable(
  lazyWithRetry(() => import("src/pages/DocumentReviewV2")),
);

const DocumentReviewRouter = () => {
  const { reviewScreenV2Enable } = useSelector(
    (state: RootState) => state.organization.org,
  );
  return reviewScreenV2Enable ? <DocumentReviewV2 /> : <FormValidation />;
};
const MailInbox = Loadable(
  lazyWithRetry(() => import("src/pages/Documents/Finance/MailInboxV2")),
);

const SendDiscrepancyToSupplier = Loadable(
  lazyWithRetry(
    () => import("src/pages/Documents/Finance/SendDiscrepancyToSupplier"),
  ),
);

const SelfTrainingReports = Loadable(
  lazyWithRetry(() => import("src/pages/SelfTrainingReports/index")),
);

const SelfTrainingReportsV2 = Loadable(
  lazyWithRetry(() => import("src/pages/SelfTrainingReportsV2/index")),
);

const ProjectDocumentsPage = Loadable(
  lazyWithRetry(
    () => import("src/pages/SelfTrainingReportsV2/documents/Documents"),
  ),
);

const SelfTrainingRouter = () => {
  const selfTrainingV2Enable = useSelector(
    (state: RootState) => state.organization.org.selfTrainingV2Enable,
  );
  return selfTrainingV2Enable ? (
    <SelfTrainingReportsV2 />
  ) : (
    <SelfTrainingReports />
  );
};

const DevelopersSettings = Loadable(
  lazyWithRetry(() => import("src/pages/developersSection/index")),
);
const AccountVerification = Loadable(
  lazyWithRetry(
    () => import("../pages/AccountVerification/AccountVerification"),
  ),
);

const Page404 = Loadable(lazyWithRetry(() => import("src/pages/Page404")));
const Page403 = Loadable(lazyWithRetry(() => import("src/pages/Page403")));

const MyAccount = Loadable(
  lazyWithRetry(() => import("src/pages/userAccount")),
);

const Components = Loadable(lazyWithRetry(() => import("../pages/Components")));
const WorkflowAnalytics = Loadable(
  lazyWithRetry(() => import("../pages/Reports/WorkflowAnalytics")),
);

const DocumentUsage = Loadable(
  lazyWithRetry(() => import("../pages/Reports/DocumentUsage")),
);
const AnalyticsV2 = Loadable(
  lazyWithRetry(() => import("../pages/plotly/index")),
);
const AnalyticsV3 = Loadable(
  lazyWithRetry(() => import("../pages/AnalyticsV3/index")),
);

const SelfTrainingReportV2 = Loadable(
  lazyWithRetry(() => import("../pages/Reports/SelfTraininigReportsv2/index")),
);
const YearWiseDocSummary = Loadable(
  lazyWithRetry(() => import("../pages/Reports/YearWiseDocSummary/index")),
);
const CustomReports = Loadable(
  lazyWithRetry(() => import("../pages/Reports/customReport/index")),
);

const CustomerPayments = Loadable(
  lazyWithRetry(() => import("../pages/Reports/CustomerPayments")),
);

const Workflows = Loadable(
  lazyWithRetry(() => import("../pages/workflows/index")),
);

const WorkflowTemplates = Loadable(
  lazyWithRetry(() => import("../pages/WorkflowTemplates")),
);

const DiscrepancySummary = Loadable(
  lazyWithRetry(() => import("../pages/Reports/DiscrepancySummary")),
);

const RedirectionPage = Loadable(
  lazyWithRetry(() => import("src/pages/integration/Component/Redirection")),
);

const PaymentGatewayOAuthRedirectionPage = Loadable(
  lazyWithRetry(
    () =>
      import(
        "src/pages/integration/businessServices/PaymentConnectors/OAuthRedirect"
      ),
  ),
);

const NotificationAppOAuthRedirectionPage = Loadable(
  lazyWithRetry(
    () =>
      import(
        "src/pages/integration/businessServices/NotificationApps/slackNotificationApp/NotificationAppOAuthRedirection"
      ),
  ),
);

const Impersonate = Loadable(
  lazyWithRetry(() => import("src/pages/Impersonate")),
);

const PreRegister = Loadable(
  lazyWithRetry(() => import("src/pages/auth/PreRegister")),
);

const Reports = Loadable(
  lazyWithRetry(() => import("src/pages/Reports/index")),
);

// expense
const Employee = Loadable(
  lazyWithRetry(() => import("@pages/Expense/Employee/Employee")),
);
const Expense = Loadable(lazyWithRetry(() => import("@pages/Expense")));

// extraction rules
const ExtractionRules = Loadable(
  lazyWithRetry(() => import("src/pages/ExtractionRules/index")),
);

// tasks
const Tasks = Loadable(lazyWithRetry(() => import("src/pages/Tasks/index")));

// dimension
const Dimension = Loadable(
  lazyWithRetry(() => import("src/pages/Dimension/index")),
);
const DimensionValues = Loadable(
  lazyWithRetry(() => import("src/pages/Dimension/DimensionValues/index")),
);

const AskDostFullPage = Loadable(
  lazyWithRetry(() => import("src/pages/askDost/AskDostFullPage")),
);

const PurchaseRequisition = Loadable(
  lazyWithRetry(
    () => import("@pages/procurement/components/procurementForm/PRForm"),
  ),
);

const ProcurementPurchaseOrder = Loadable(
  lazyWithRetry(
    () => import("@pages/procurement/components/procurementForm/POForm"),
  ),
);
